'use strict';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject} from '@angular/core';
import {CurationSettings} from '../../curation-dashboard/curation-setting';
import {CurationActionCreatorService} from '../../../services/curation-action-creator.service';
import {CurationEditorAbstract} from '../curation-editor-abstract';
import {DynamicModalLoaderService} from '@ispm/layout/services/dynamic-modal-loader.service';
import {WorkflowBarActionsCreatorService} from '@ispm/shared/components/workflow-bar/services/workflow-bar-actions-creator.service';
import {FormHandlerService} from '@ispm/dynamic-form/services/form-handler.service';
import {IgenomicsState} from '@ispm/core/interfaces/genomics-state.interface';
import {Store} from '@ngrx/store';
import {FORM_ACTIONS} from '@ispm/dynamic-form/store/form.actions';
import {IdynamicFormItem} from '@ispm/shared/dynamic-form/interfaces/IdynamicFormItem';
import {CurationUtils} from '../../../utils/curation-utils';
import {filter, first, take} from 'rxjs/operators';
import {PdfService} from '@ispm/dashboard/test/services/pdf.service';
import {PanelsService} from "@ispm/layout/services/panels.service";
import {CoreActionCreatorService} from "@ispm/core/services/core-action-creator.service";
import {cloneDeep, get, has, isEqual, set, unset} from 'lodash';
import {
  ARIA_TEMPLATES_PROP,
  CITATIONS_PROP,
  Discipline,
  DRUG_DISPLAY_NAME,
  DRUG_S,
  DRUGS_PROP, RATIONALIZATION_PATHWAY_NAME_S, RATIONALIZATION_PROP,
  THERAPIES_PROP,
  THERAPY_DISPLAY_NAME,
  THERAPY_S
} from "@ispm/shared/constants/curation_constants";
import {TranslateService} from "@ngx-translate/core";
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {DiagnosticOrderService} from '@ispm/dashboard/test/services/diagnostic-order.service';
import {ActivityService} from '@ispm/user/services/activity.service';
import {WorkflowConfigService} from "@ispm/workflow/services/workflow-config.service";
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {NotesService} from '@ispm/user/services/notes.service';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {HttpInterceptorService} from "@ispm/core/services/http-interceptor.service";
import {ActivatedRoute, Router} from "@angular/router";
import {RouterManagerService} from "@ispm/core/services/router-manager.service";
import {CURATION_EDITOR_ACTIONS} from "../../../store/curation.editor.actions";

const USER_CREATED_TAG = 'TreatmentPlan_UserCreated';

@Component({
  selector: 'gen-curation-editor-treatment-plan-component',
  templateUrl: './curation-editor-treatment-plan-component.html',
  styleUrls: ['./curation-editor-treatment-plan-component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class CurationEditorTreatmentPlanComponent extends CurationEditorAbstract {
  public formName: string;
  public itemsFormName: string;
  public citationsFormName: string;
  public ariaTemplatesFormName: string;
  public userCreatedTagPrefix: string = USER_CREATED_TAG;
  public userCreatedTagsSet = [CurationSettings.CREATE_TAG_USER_CREATED,CurationSettings.CREATE_TAG_MANUALLY_CREATED];
  public revisedEntitiesConf: any;
  public isRadiationDiscipline: boolean = false;
  private store: any;
  private isDetailsFormValid: boolean = true;
  private nameField: string = 'name';

  constructor(public authService: AuthenticationService,
              formHandler: FormHandlerService,
              public notificationMessageService: NotificationMessageService,
              workflowBarActionsCreatorService: WorkflowBarActionsCreatorService,
              modalLoader: DynamicModalLoaderService,
              activatedRoute: ActivatedRoute,
              panelService: PanelsService,
              curationActionCreatorService: CurationActionCreatorService,
              translateService: TranslateService,
              protected diagnosticOrderService : DiagnosticOrderService,
              detector: ChangeDetectorRef,
              store: Store<IgenomicsState>,
              pdfService: PdfService,
              notes: NotesService,
              coreAC: CoreActionCreatorService,
              public userActionCreatorService: UserActionCreatorService,
              public activityService: ActivityService,
              workflowConfigService: WorkflowConfigService,
              _http: HttpInterceptorService,
              router: Router,
              routerManagerService: RouterManagerService) {
    super(authService, formHandler, notificationMessageService, workflowBarActionsCreatorService, modalLoader,
          activatedRoute, panelService, notes, coreAC, curationActionCreatorService, translateService,
          diagnosticOrderService, userActionCreatorService, activityService,_http, router, pdfService, detector, workflowConfigService, routerManagerService);
    this.store = store.select('forms');
  }

  setInitParams() {
    if (this.relevantProperty) {
      this.isRadiationDiscipline = isEqual(get(this.relevantProperty, 'property', undefined), 'discipline.display')
        && isEqual(get(this.relevantProperty, 'value', undefined), Discipline.RADIATION_ONCOLOGY);
    }
    this.formName = this.editorFormsMap['details'].name;
    this.citationsFormName = this.editorFormsMap['citations'].name;

    //this.drugsAllowDuplicate = this.editorFormsMap[DRUGS_PROP].allowDuplicate;
    if (this.isRadiationDiscipline) {
      this.itemsFormName = get(this.editorFormsMap, THERAPIES_PROP + '.' + this.nameField, undefined);
      this.nameField = has(this.editorFormsMap, THERAPIES_PROP) ?
        this.editorFormsMap[THERAPIES_PROP]['therapyDisplayNameField'] || THERAPY_DISPLAY_NAME : undefined;
      this.formHandler.getForm(this.editorFormsMap[THERAPIES_PROP].name).subscribe();
      this.ariaTemplatesFormName = this.editorFormsMap['ariaTemplates'].name;
    }
    else {
      this.itemsFormName = get(this.editorFormsMap, DRUGS_PROP + '.' + this.nameField, undefined);
      this.nameField = has(this.editorFormsMap, DRUGS_PROP) ?
        this.editorFormsMap[DRUGS_PROP]['drugDisplayNameField'] || DRUG_DISPLAY_NAME : undefined;
      this.formHandler.getForm(this.editorFormsMap[DRUGS_PROP].name).subscribe();
    }

    this.revisedEntitiesConf = this.editorFormsMap['revisedEntities'];
    this.curationActionCreatorService.getState(['definitions'])
      .take(1)
      .subscribe((data => {
        this.confMap = Array.from(data.values()).filter(data => data['type'] === this.curationType || data['type'] === 'curationKB');
      }));
  }

  prepareDataToSave(status: string): Promise<boolean> {
    const confRoot = this.confMap.filter(data => data['type'] === CurationSettings.CURATION_KB)[0];
    CurationUtils.updateRelatedEntities(confRoot, this.curationItem.curationData);

    // Drugs order should be saved by their priority - see:
    //    (GEN-31605) drag and drop of drugs in the treatment plan is not working
    // this.sortDrugsArray('display');

    //As the 'Pathway name' field represents by a multiselect component we have to initialize it by an empty string array before the first data saving
    if (has(this.content, RATIONALIZATION_PROP) && !has(this.content, RATIONALIZATION_PATHWAY_NAME_S)) {
      set(this.content, RATIONALIZATION_PATHWAY_NAME_S, Array<string>());
    }

    // removing empty citations, drugs, therapies
    if (has(this.content, CITATIONS_PROP)) {
      set(this.content, CITATIONS_PROP,
        this.cleanData(get(this.content, CITATIONS_PROP, undefined), 'articleLink'));
    }

    if (has(this.content, ARIA_TEMPLATES_PROP)) {
      set(this.content, ARIA_TEMPLATES_PROP,
        this.cleanData(get(this.content, ARIA_TEMPLATES_PROP, undefined), 'ariaTemplate'));
    }

    // remove empty drugs
    if (has(this.content, DRUGS_PROP)) {
      set(this.content, DRUGS_PROP,
        this.cleanData(get(this.content, DRUGS_PROP, undefined), DRUG_S));
      if (this.content[DRUGS_PROP].length > 0) {
        // Saving 'drugDisplayName' if missing.
        // 'drugDisplayName' is missing when manually adding drugs.
        let drugsArr = this.content[DRUGS_PROP];
        for(let drugData of drugsArr) {
          if (!drugData[DRUG_DISPLAY_NAME]) {
            drugData[DRUG_DISPLAY_NAME] = drugData[DRUG_S]['display'];
          }
          if(drugData[DRUG_DISPLAY_NAME] != drugData[DRUG_S]['display']) {
            drugData[DRUG_DISPLAY_NAME] = drugData[DRUG_S]['display'];
          }
        }
      }
      else {
        delete this.content[DRUGS_PROP];
      }
    }
    // remove empty therapies
    if (has(this.content, THERAPIES_PROP)) {
      set(this.content, THERAPIES_PROP,
        this.cleanData(get(this.content, THERAPIES_PROP, undefined), THERAPY_S));
      if (this.content[THERAPIES_PROP].length > 0) {
        // Saving 'therapyDisplayName' if missing.
        // 'therapyDisplayName' is missing when manually adding therapies.
        this.content[THERAPIES_PROP].forEach((drugData) => {
          if (!drugData[THERAPY_DISPLAY_NAME] && drugData[THERAPY_S]) {
            drugData[THERAPY_DISPLAY_NAME] = drugData[THERAPY_S]['display'];
          }
        });
      }
      else {
        delete this.content[THERAPIES_PROP];
      }
    }
    // TODO: do we need to set something as drugDisplayName for therapies ?

    // Remove deleted drugs and citation before approval
    if (this.curationStatus == CurationSettings.CURATION_STATUS_WAITING_FOR_APPROVAL || status == CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED) {
      return this.removeDeletedItems([DRUGS_PROP, THERAPIES_PROP, CITATIONS_PROP,ARIA_TEMPLATES_PROP],
        this.curationStatus, status != CurationSettings.CURATION_STATUS_PUBLISHED_SUBMITTED).then(() => {
        return Promise.resolve(true);
      }).catch((err) => {
        console.error(err);
        return Promise.reject(err);
      });
    }
    else {
      return Promise.resolve(true);
    }
  }

  public getCurationKey(): string {
    return this.content['externalAutoGen'] + ' + ' + this.content['treatmentPlanName'];
  }

  updateForm(form, updateFieldKeys: any) {
    if (form && updateFieldKeys) {
      for (const updatedField of updateFieldKeys) {
        const fieldValue = get(form, updatedField);
        set(this.content, updatedField, fieldValue);
      }
      this.curationKey = this.getCurationKey();

      // Updating drugs list if regimen data (version) was changed
      const regimenDataChanged: boolean = Array.isArray(updateFieldKeys) && updateFieldKeys.includes('regimen');
      if (regimenDataChanged) {
        // we need to restore dose, cycle ets...  for drugs which left into Regimen drug list.
        let originalTreatmentData = this.isRadiationDiscipline ? form.therapies: form.drugs ;
        let treatmentInformationBeforeChange = originalTreatmentData ? [...originalTreatmentData] : [];

        // list data path contains path to data
        if (has(this.curationData, this.listDataPath)) {
          unset(this.curationData, this.listDataPath);
        }

        if (form.regimen && form.regimen.code) {
          this.copyDataFromRelatedCurations(true, false, form.regimen.code, this.editorFormsMap['itemsToCopy'])
            .then(() => {
              // GEN-74990 Regimen version was changed, since that it doesn't matter what was before,
              // we keep only data for grugs that stayed and removed any other which have been added by user
              // or removed from the new regimen revision
              // GEN-101491: in case revised regimen contains the same drugs we would like to load back dose.. cycle .. etc.
              if(!this.isRadiationDiscipline) {
                this.drugs = this.updateTreatmentItemsInfo(this.drugs, treatmentInformationBeforeChange, 'drug');
                super.setListData(this.drugs);
              }
              else {
                this.therapies = this.updateTreatmentItemsInfo(this.therapies, treatmentInformationBeforeChange, 'therapy');
                super.setListData(this.therapies);
              }
              this.createItemsDropdown();

            }).catch((err) => {
            console.error(err);
          });
        } else { // if regimen was deleted, remove it from the object
          delete form.regimen;
        }
      }
    }
  }

  /*
   * GEN-101491
   * This is called when the revision of the regimen is updated in the treatment plan.
   * During the update, the drugs (/therapies) table is cleared and then filled-in with just
   * the names of the drugs in the new revision of the regimen, without any details.
   * This function restores all the drug lines that existed before the update, only for those
   * drugs that exist also in the new revision of the regimen.
   * The resulting order of the lines is the following:
   * First, all the drugs that were *added* in the new revision of the regimen.
   * Then, all the lines that existed before the update, in the same order as before, only
   * for the drugs that still exist in the new revision.
   */
  private updateTreatmentItemsInfo(items, itemsInformationBeforeChange, type) : Array<any>
  {
    if (!!items) {
      if (!!itemsInformationBeforeChange) {
        const codeFieldName = type + '.code';
        const displayFieldName = type + '.display';
        let oldItemsToKeep = [];
        itemsInformationBeforeChange.forEach(oldItemObj => {
          for (const newItem of items) {
            if (get(oldItemObj, codeFieldName) === get(newItem, codeFieldName)) {
              // Copy drug / therapy name from regimen
              const newItemName = get(newItem, displayFieldName);
              set(oldItemObj, displayFieldName, newItemName);
              set(oldItemObj, this.nameField, newItemName);
              oldItemsToKeep.push(oldItemObj);
              break;
            }
          }
        });
        let newItems = items.filter(newItem =>
          !itemsInformationBeforeChange.some(oldItemObj => get(oldItemObj, codeFieldName) === get(newItem, codeFieldName)));
        return [...newItems, ...oldItemsToKeep];
      }
      return items;
    }
    return [];

  }

  validationEvent(valid) {
    if (typeof valid === 'boolean') {
      this.isDetailsFormValid = valid;
      this.calculateValidationState();
    }
  }

  postGetCurationItem() {
    this.copyDataFromRelatedCurations(false, true, undefined, this.editorFormsMap['itemsToCopy']).then(() => {
      this.createItemsDropdown();
    }).catch((err) => {
      console.error(err);
    });
  }

  postUpdateCurationItem() {
    this.updateForm(this.content, null);
  }

  itemUpdate(items: any) {
    if (this.isRadiationDiscipline) {
      this.content[THERAPIES_PROP] = items.dataArray;
    } else {
      this.content[DRUGS_PROP] = items.dataArray;
    }
    this.createRowItemsDropdown(items.rowData);
  }

  async changeStatus(status: string, callback?: any) {
    if (status === CurationSettings.CURATION_STATUS_DRAFT && this.isPublishedStatus(this.curationStatus) && this.rationalization) {
      /* GEN-191193 - Since the TP rationalization data belongs to the particular revision, it must be cleared for a new revision*/
      this.removeRationalizationData();
    }

    await this.getBypassFormSubmittingValidationStatus(status, callback);
  }

  private async getBypassFormSubmittingValidationStatus(status: string, callback?: any) {
    let invalidControls = [];
    let editorAction = this.curationActionCreatorService.getLastEditorAction();
    if (!this.isValid && (editorAction === CURATION_EDITOR_ACTIONS.SAVE || editorAction === CURATION_EDITOR_ACTIONS.SUBMIT_FOR_REVIEW ||
      editorAction === CURATION_EDITOR_ACTIONS.ARCHIVE) && has(this.editorFormsMap, RATIONALIZATION_PROP)) {
      this.formHandler.getFormControls(this.editorFormsMap[RATIONALIZATION_PROP].name).pipe(first())
        .subscribe((formControls) => {
          if (!formControls) {
            super.changeStatus(status, callback, false);
            return;
          }

          invalidControls = formControls.controls.filter(ctrl => ctrl.status === 'INVALID');
          this.formHandler.getStoredFormById(this.editorFormsMap[RATIONALIZATION_PROP].name).pipe(first())
            .subscribe((formConfig) => {
              if (!formConfig) {
                super.changeStatus(status, callback, false);
                return;
              }

              let bypassFormSubmittingValidation = true;
              invalidControls.forEach(ctrl => {
                let input = formConfig.find(input => input.name == ctrl.name);
                if (!input || !input.required || (input.required && !input.requiredForPublish && !ctrl.value)) {
                  bypassFormSubmittingValidation = false;
                  return;
                }
              });

              super.changeStatus(status, callback, bypassFormSubmittingValidation);
            });
        });
    } else {
      await super.changeStatus(status, callback, false);
    }
  }

  citationUpdate(citationsList: any) {
    this.content[CITATIONS_PROP] = citationsList;
  }

  ariaTemplatesUpdate(ariaTemplatesList: any) {
    this.content[ARIA_TEMPLATES_PROP] = ariaTemplatesList;
  }

  public itemOverridesDrug(value): any {
    const allowEdit: boolean = value[USER_CREATED_TAG] === true;
    const formOverrides: { [itemName: string]: Partial<IdynamicFormItem> } =  {'drug': {'attributes': {'previewOnly': !allowEdit}}};
    return formOverrides;
  }

  public itemOverridesTherapy(value){
    const allowEdit: boolean = value[USER_CREATED_TAG] === true;
    const formOverrides: { [itemName: string]: Partial<IdynamicFormItem> } = {'therapy': {'attributes': {'previewOnly': !allowEdit}}};
    return formOverrides;
  }

  public getItemOverrides(){
    return this.isRadiationDiscipline ? this.itemOverridesTherapy: this.itemOverridesDrug;
  }

  //Returns array with related entity (regimen) curationItemId
  public getCurrentRelatedEntitiesIds(): Array<string> {
    let itemsArray = [];
    if (this.content['regimen']) {
      itemsArray.push(this.content['regimen']['code']);
    }
    return itemsArray;
  }

  private calculateValidationState() {
    let valid: boolean = true;
    this.validationFormsMap.forEach((value: boolean, key: string) => {
      if (value == false) {
        valid = false;
      }
    });
    this.isValid = valid && this.isDetailsFormValid;
    this.calculateState();
  }

  private createRowItemsDropdown(rowData: any[]) {
    if(!!rowData) {
      const values = [];
      const existingItems = [];
      const itemsData = super.getListData();
      if (!!itemsData) {
        itemsData.forEach((data) => {
          this.addValuesItemMenu(existingItems, data, values);
        });
      }
      const itemName = this.isRadiationDiscipline ? THERAPY_S : DRUG_S;
      rowData.forEach(activity => {
        if (activity.name === itemName) {
          activity['type'] = 'dropdown';
          activity['options']['values'] = values;
          activity['options']['resource'] = null;
        }
      });
    }
  }

  private createItemsDropdown() {
    const values = [];
    const existingItems = [];
    const itemsData = super.getListData();
    if (!!itemsData) {
      itemsData.forEach((data) => {
        this.addValuesItemMenu(existingItems, data, values);
      });
    }
    this.formHandler.getStoredFormById(this.itemsFormName)
      .pipe(
        filter(form => !!form),
        take(1))
      .subscribe((data) => {
          const itemName = this.isRadiationDiscipline ? THERAPY_S : DRUG_S;
          data.forEach(activity => {
            if (activity.name === itemName) {
              activity['type'] = 'dropdown';
              activity['options']['values'] = values;
              activity['options']['resource'] = null;
              this.store.dispatch({
                type: FORM_ACTIONS.ADD_FORM,
                payload: {
                  name: this.itemsFormName,
                  inputs: cloneDeep(data)
                }
              });
              this.dataLoaded();
            }
          })
        }
      );
  }

  private addValuesItemMenu(existedItems: any[], data, values: any[]) {
    const item = {};
    let field = this.isRadiationDiscipline ? THERAPY_S : DRUG_S;
    if(!!data) {
      let display = get(data, field.concat('.').concat(this.nameField)) || get(data, this.nameField) || get(data, field.concat('.display'));
      Object.assign(item, data);
      if(has(item, field)) {
        item[field]['display'] = display;
        if (existedItems.indexOf(display) === -1) {
          values.push(item[field]);
          existedItems.push(display);
        }
      }
    }
  }

  private dataLoaded() {
    this.isDataLoaded = true;
    this.detector.markForCheck();
  }

  private removeRationalizationData(){
    delete this.content[RATIONALIZATION_PROP];
  }
}
